import { useTheme } from "@emotion/react";
import type { Annotation } from "@rowan/prisma";
import { useCallback, useMemo, useRef } from "react";
import { mixHighlightColour } from "../../design/colours";
import type { Annotation as CurrentAnnotation } from "../bits/AnnotationMenu";
import { pageColourOptions } from "../bits/settings/defaults";
import { getAnnotationId, getParentElementWithId } from "../utils";

export const useTextHighlight = (
  textContainerRef: React.RefObject<HTMLElement | null>,
  colour?: string,
) => {
  const theme = useTheme();
  const isDark = colour === pageColourOptions.black.background;
  const highlightsRef = useRef(new Map<string, HTMLDivElement>());
  const borderRef = useRef(new Map<string, HTMLDivElement>());
  const highlightColours = useMemo(
    () => ({
      selected: mixHighlightColour(
        colour || theme.colours.accent.light._15,
        theme.colours.highlight.blue,
      ),
      default: mixHighlightColour(
        colour || theme.colours.accent.light._15,
        theme.colours.highlight.yellow,
      ),
      highlighted: isDark
        ? theme.colours.highlight.darkYellow
        : mixHighlightColour(
            colour || theme.colours.accent.light._15,
            theme.colours.highlight.yellow,
            90,
          ),
    }),
    [
      colour,
      theme.colours.highlight.blue,
      theme.colours.highlight.yellow,
      theme.colours.highlight.darkYellow,
      theme.colours.accent.light._15,
      isDark,
    ],
  );

  const applyHighlight = useCallback(
    (range: Range, id: string) => {
      const container = textContainerRef.current;
      if (!container || !id) return;
      const existing = highlightsRef.current.get(id);
      if (existing) {
        existing.remove();
        highlightsRef.current.delete(id);
      }

      const rects = range.getClientRects();
      if (!rects.length) return;

      const highlightDiv = document.createElement("div");
      highlightDiv.className = "highlight-overlay";
      highlightsRef.current.set(id, highlightDiv);

      const containerRect = container.getBoundingClientRect();
      const scrollTop = container.scrollTop;
      const scrollLeft = container.scrollLeft;

      let firstSpanIdAssigned = false;
      Array.from(rects).forEach((rect) => {
        const span = document.createElement("span");
        const isAdditionalPadding = rect.height > 200;
        if (!isAdditionalPadding) {
          if (!firstSpanIdAssigned) {
            span.id = `highlight-${id}`;
            firstSpanIdAssigned = true;
          }
          span.className = "highlighted";
          span.style.position = "absolute";
          span.style.left = `${rect.left - containerRect.left + scrollLeft}px`;
          span.style.top = `${rect.top - containerRect.top + scrollTop}px`;
          span.style.width = `${rect.width}px`;
          span.style.height = `${rect.height}px`;
          span.style.backgroundColor =
            id === "selected"
              ? highlightColours.selected
              : highlightColours.default;
          span.style.opacity = "1";
          span.style.pointerEvents = "none";
          span.style.zIndex = id === "selected" ? "-1" : "-2";
          highlightDiv.appendChild(span);
        }
      });

      container.appendChild(highlightDiv);
    },
    [textContainerRef, highlightColours],
  );

  const clearHighlight = useCallback((id?: string) => {
    if (id) {
      const highlight = highlightsRef.current.get(id);
      if (highlight) {
        highlight.remove();
        highlightsRef.current.delete(id);
      }
    } else {
      highlightsRef.current.forEach((el) => el.remove());
      highlightsRef.current.clear();
    }
  }, []);

  const applyBorder = useCallback(
    (range: Range, id: string) => {
      const container = textContainerRef.current;
      if (!container || !id) return;

      const existing = borderRef.current.get(id);
      if (existing) {
        existing.remove();
        borderRef.current.delete(id);
      }

      const rects = range.getClientRects();
      if (!rects.length) return;

      const borderDiv = document.createElement("div");
      borderDiv.className = "border-highlight";

      const containerRect = container.getBoundingClientRect();
      const scrollTop = container.scrollTop;
      const scrollLeft = container.scrollLeft;

      Array.from(rects).forEach((rect) => {
        const isAdditionalPadding = rect.height > 200;
        if (!isAdditionalPadding) {
          const span = document.createElement("span");
          span.className = "highlighted";
          span.style.position = "absolute";
          span.style.left = `${rect.left - containerRect.left + scrollLeft}px`;
          span.style.top = `${rect.top - containerRect.top + scrollTop}px`;
          span.style.width = `${rect.width}px`;
          span.style.height = `${rect.height}px`;
          span.style.backgroundColor = highlightColours.highlighted;
          span.style.opacity = "1";
          span.style.pointerEvents = "none";
          span.style.zIndex = "-1";
          borderDiv.appendChild(span);
        }
      });
      container.appendChild(borderDiv);
      borderRef.current.set(id, borderDiv);
    },
    [textContainerRef, highlightColours.highlighted],
  );

  const clearBorder = useCallback((id?: string) => {
    if (id) {
      const border = borderRef.current.get(id);
      if (border) {
        border.remove();
        borderRef.current.delete(id);
      }
    } else {
      borderRef.current.forEach((el) => el.remove());
      borderRef.current.clear();
    }
  }, []);

  const serializeRange = useCallback(
    (annotation: CurrentAnnotation): Annotation["range"] | void => {
      const { range } = annotation;
      const startNode = range.startContainer;
      const startNodeId = getParentElementWithId(startNode);
      const findCommonAncestorWithId = (
        a: Node | null,
        b: Node | null,
      ): HTMLElement | null => {
        const isElementWithId = (n: Node): n is HTMLElement =>
          n.nodeType === 1 && (n as HTMLElement).id !== "";
        const seen = new Set<Node>();
        let currA: Node | null = a;
        while (currA) {
          seen.add(currA);
          currA = currA.parentNode;
        }
        let currB: Node | null = b;
        while (currB) {
          if (seen.has(currB) && isElementWithId(currB)) return currB;
          currB = currB.parentNode;
        }
        return null;
      };

      const container = findCommonAncestorWithId(
        range.startContainer,
        range.endContainer,
      );
      const containerId = container?.id;
      if (!container || !containerId) return;

      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      let offset = 0;
      let start = -1;
      let end = -1;

      while (walker.nextNode()) {
        const node = walker.currentNode as Text;
        const len = node.textContent?.length ?? 0;

        if (start === -1 && node === range.startContainer) {
          start = offset + range.startOffset;
        }

        if (end === -1 && node === range.endContainer) {
          end = offset + range.endOffset;
        }

        offset += len;
      }

      // Fallback if end wasn't found explicitly
      if (start !== -1 && end === -1) {
        end = start + range.toString().length;
      }

      if (start === -1 || end === -1) return;

      return {
        startOffset: start,
        endOffset: end,
        startNodeId: startNodeId || containerId,
        containerId,
      };
    },
    [],
  );

  const deserializeRange = useCallback(
    (range: Annotation["range"]): Range | null => {
      const { startOffset, endOffset, containerId } = range;
      const container = document.getElementById(containerId);
      if (!container) return null;

      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
      let offset = 0;

      let startNode: Text | null = null;
      let endNode: Text | null = null;
      let startInNode = 0;
      let endInNode = 0;

      while (walker.nextNode()) {
        const node = walker.currentNode as Text;
        const len = node.textContent?.length ?? 0;

        // Find start node
        if (!startNode && offset + len >= startOffset) {
          startNode = node;
          startInNode = startOffset - offset;
        }

        // Find end node
        if (!endNode && offset + len >= endOffset) {
          endNode = node;
          endInNode = endOffset - offset;
        }

        offset += len;
      }

      if (!startNode || !endNode) {
        return null;
      }

      const newRange = document.createRange();
      newRange.setStart(startNode, startInNode);
      newRange.setEnd(endNode, endInNode);
      return newRange;
    },
    [],
  );

  const applyHighlights = useCallback(
    (annotations: Annotation[], focusedId?: string) => {
      const newIds = new Set<string>();

      annotations.forEach((annotation) => {
        const id = getAnnotationId(annotation);
        newIds.add(id);
        const range = deserializeRange(annotation.range);
        if (range) {
          applyHighlight(range, id);
          if (focusedId === id) {
            applyBorder(range, id);
          }
        }
      });

      highlightsRef.current.forEach((_, id) => {
        if (!newIds.has(id)) {
          clearHighlight(id);
        }
      });

      if (borderRef.current) {
        borderRef.current.forEach((_, id) => {
          if (!newIds.has(id)) {
            clearBorder(id);
          }
        });
      }
    },
    [
      applyHighlight,
      applyBorder,
      clearHighlight,
      clearBorder,
      deserializeRange,
    ],
  );

  return {
    applyHighlight,
    clearHighlight,
    serializeRange,
    deserializeRange,
    highlightsRef,
    applyBorder,
    clearBorder,
    applyHighlights,
  };
};
